import type { ArcadeScreen } from "../ScreenManager.js";
import type { MenuAction } from "../MenuInput.js";
import { uiSfx } from "../uiSfx.js";
import { createBootAdvance, type BootAdvance } from "./bootAdvance.js";

/**
 * BOOT — fake cabinet power-on (menu-redesign-plan §3.1). ~0.8 s CRT flicker
 * (boot.css) + logo reveal + a version/ping line fed by a non-blocking
 * `/health` fetch. ANY input skips; otherwise auto-advances to TITLE.
 */

export interface BootScreenDeps {
  version: string;
  /** Absolute http(s) URL of the server /health endpoint, or null to skip the check. */
  healthUrl: string | null;
  /** Advance to TITLE. Guaranteed to fire exactly once per enter. */
  onDone: () => void;
  /** Total boot duration before auto-advance (ms). */
  durationMs?: number;
  fetchFn?: typeof fetch;
}

const BOOT_MS = 2300;
const PING_TIMEOUT_MS = 4000;

export class BootScreen implements ArcadeScreen {
  private adv: BootAdvance | null = null;
  private pingEl: HTMLElement | null = null;
  private pingStarted = false;

  constructor(private readonly deps: BootScreenDeps) {}

  mount(root: HTMLElement): void {
    root.innerHTML = `
      <div class="boot-inner">
        <div class="boot-logo">BANG<span class="boot-logo-bop">BOP</span>TANKS</div>
        <div class="boot-sysline">SOLVX ARCADE SYSTEM — v${this.deps.version}</div>
        <div class="boot-ping">SERVER LINK …</div>
      </div>
      <div class="boot-skip-hint">PRESS ANY KEY</div>`;
    this.pingEl = root.querySelector(".boot-ping");
    root.addEventListener("pointerdown", () => this.skip());
  }

  onEnter(): void {
    this.adv = createBootAdvance(() => this.deps.onDone());
    this.adv.arm(this.deps.durationMs ?? BOOT_MS);
    this.checkHealth();
  }

  onLeave(): void {
    this.adv?.dispose();
    this.adv = null;
  }

  handleInput(_action: MenuAction): boolean {
    this.skip();
    return true;
  }

  private skip(): void {
    if (this.adv?.skip()) uiSfx.play("confirm");
  }

  /** Non-blocking: the boot never waits for this, it just colours the ping line. */
  private checkHealth(): void {
    const el = this.pingEl;
    if (this.pingStarted || !this.deps.healthUrl || !el) return;
    this.pingStarted = true;

    const fetchFn =
      this.deps.fetchFn ?? (typeof fetch !== "undefined" ? fetch.bind(globalThis) : null);
    if (!fetchFn) {
      el.textContent = "";
      return;
    }

    const t0 = Date.now();
    const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timeout = ctrl ? setTimeout(() => ctrl.abort(), PING_TIMEOUT_MS) : null;

    fetchFn(this.deps.healthUrl, ctrl ? { signal: ctrl.signal } : undefined)
      .then((res) => {
        el.dataset["state"] = res.ok ? "ok" : "down";
        el.textContent = res.ok
          ? `SERVER LINK OK — ${Date.now() - t0}MS`
          : `SERVER LINK — HTTP ${res.status}`;
      })
      .catch(() => {
        el.dataset["state"] = "down";
        el.textContent = "SERVER LINK — OFFLINE";
      })
      .finally(() => {
        if (timeout !== null) clearTimeout(timeout);
      });
  }
}
